import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import type { PhotoItemType, VideoItemType } from "./types";
import { MediaEnum } from "utils";

dayjs.extend(utc);

// Type guard to check if an item is a VideoItemType
// Checks for VideoItemType-specific properties: uploaded as number and recordDate presence
export function isVideoItem(item: PhotoItemType | VideoItemType): item is VideoItemType {
  return item.mediaType === MediaEnum.Video && 
         'recordDate' in item && 
         typeof item.uploaded === 'number';
}

export function getTimeStringFromSeconds(number: number) {
  const hours = Math.floor(number / 3600);
  const minutes = Math.floor((number - hours * 3600) / 60);
  const seconds = Math.floor(number - (hours * 3600 + minutes * 60));

  const getFormattedNumber = (num: number) => `${num}`.padStart(2, "0");

  if (hours === 0) {
    return `${getFormattedNumber(minutes)}:${getFormattedNumber(seconds)}`;
  }

  return `${getFormattedNumber(hours)}:${getFormattedNumber(minutes)}:${getFormattedNumber(seconds)}`;
}

export function getConvertedPhotoItems(res: any) {
  return res.reduce((acc: any[], item: any) => {
    // Check if this photo item is actually a video (from backend MediaType)
    const isVideo = item.mediaType === 'video';
    
    if (isVideo) {
      // Convert to VideoItemType format for video items stored in Photo table
      acc.push({
        ...item,
        id: item.id,
        recordDate: item.takenOnUtc,
        width: item.width,
        height: item.height,
        fileLength: item.fileLength,
        uploaded: item.fileLength,
        url: item.processedUrl,
        thumbnail: item.processedThumbnailUrl,
        date: item.takenOnUtc,
        mediaType: MediaEnum.Video,
        name: `${item.id}.mp4`,
        seenOnUtc: item.seenOnUtc,
        duration: item.videoDurationSeconds 
          ? getTimeStringFromSeconds(item.videoDurationSeconds) 
          : '00:00',
        userConsent: item.userConsent,
      });
    } else {
      // Regular photo item
      acc.push({
        ...item,
        date: item.takenOnUtc,
        mediaType: MediaEnum.Photo,
        url: item.processedUrl,
        thumbnail: item.processedThumbnailUrl,
        name: `${item.id}.${item.fileExtension}`,
      });
    }
    return acc;
  }, []);
}

export function getConvertedVideoItems(res: any) {
  return res.map((item: any) => {
    return {
      ...item,
      url: item.videoUrl,
      thumbnail: item.thumbnailUrl,
      date: item.recordDate,
      mediaType: MediaEnum.Video,
      name: `${item.id}.mp4`, // @TODO: fix
    };
  });
}

export function getBackgroundGroupKey(fileName: string | null): string | null {
  if (!fileName) return null;
  if (fileName.includes("_")) {
    return fileName.split("_")[0];
  }
  return fileName;
}

/**
 * iOS (including iPadOS, which reports itself as macOS with a touch screen).
 *
 * Matters for downloads: iOS Safari only allows one download per user gesture.
 * A second save started from JavaScript after an await no longer counts as
 * user-initiated, so it either aborts the previous download or prompts
 * "Start new download? This will stop all progress for your current download."
 * Downloading several files one after another is therefore impossible there,
 * no matter how they are spaced out - only a single zip works.
 */
export function isIosDevice(): boolean {
  if (typeof navigator === "undefined") return false;

  return (
    /iP(hone|ad|od)/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}
